import React, { useState, useRef, useEffect } from "react";
import {
  Collapse,
  Input,
  Button,
  Flex,
  Form,
  Switch,
  Typography,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import globalData from "../../../config/global_data";

const { Text } = Typography;

const CreateKitAttributeList = ({ setSavedattributes, customfielddata = null, resetToken = 0 }) => {
  // initialize from globalData to avoid setting state in an effect on every render
  const [attributes, setAttributes] = useState(() =>
    globalData && globalData.attribute_tree && globalData.attribute_tree.length > 0
      ? globalData.attribute_tree
      : []
  );

  // sync attributes to parent only when attributes change
  useEffect(() => {
    setSavedattributes({ custom_fields: attributes });
  }, [attributes, setSavedattributes]);

  // reset all attribute values to empty when resetToken changes (after successful create)
  useEffect(() => {
    if (!resetToken) return;
    setAttributes(prev => {
      const cleared = prev.map(attr => ({
        ...attr,
        values: Array.isArray(attr.values) && attr.values.length > 0
          ? attr.values.map(() => "")
          : [""]
      }));
        if (globalData && Array.isArray(globalData.attribute_tree)) {
          globalData.attribute_tree = cleared;
        }
      return cleared;
    });
  }, [resetToken]);

  const dragItem = useRef();
  const dragOverItem = useRef();

  const handleSort = () => {
    const currentItem = attributes[dragItem.current];
    const updatedItems = [...attributes];
    updatedItems.splice(dragItem.current, 1);
    updatedItems.splice(dragOverItem.current, 0, currentItem);

    setAttributes(updatedItems);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const addAttribute = () => {
    setAttributes([
      ...attributes,
      {
        name: `New Attribute ${attributes.length + 1}`,
        values: [""],
        isEditable: true,
      },
    ]);
  };

  const updateAttributeName = (index, newName) => {
    const updated = [...attributes];
    updated[index].name = newName;
    setAttributes(updated);
  };

  const updateValue = (attrIndex, valIndex, newValue) => {
    const updated = [...attributes];
    updated[attrIndex].values[valIndex] = newValue;
    setAttributes(updated);
  };

  const addValue = (attrIndex) => {
    const updated = [...attributes];
    updated[attrIndex].values.push("");
    setAttributes(updated);
  };

  const removeValue = (attrIndex, valIndex) => {
    const updated = [...attributes];
    updated[attrIndex].values.splice(valIndex, 1);
    setAttributes(updated);
  };

  const removeAttribute = (index) => {
    const updated = attributes.filter((_, i) => i !== index);
    setAttributes(updated);
  };

  // Build Collapse items for attributes (uses label + children so we can attach drag handlers on label)
  const attributeItems = attributes.map((attribute, index) => {
    const isDraggable = attribute.name !== "Main Information";

    const label = (
      <div
        style={{ display: "flex", alignItems: "center", width: "100%", cursor: isDraggable ? "move" : "default" }}
        {...(isDraggable && {
          draggable: true,
          onDragStart: (e) => {
            dragItem.current = index;
            // allow drag image default
          },
          onDragEnter: () => {
            dragOverItem.current = index;
          },
          onDragEnd: handleSort,
          onDragOver: (e) => e.preventDefault(),
        })}
      >
        {attribute.name}
      </div>
    );

    const children = (
      <div>
        <Form.Item
          label={
            <span>
              Attribute Name&nbsp;                      
            </span>
          }
          style={{ marginBottom: 8 }}
          name={attribute.name}
        >
          <Input
            value={attribute.name}
            onChange={(e) => updateAttributeName(index, e.target.value)}
            placeholder={attribute.name}
            suffix={!attribute.isEditable && <LockOutlined />}
            disabled={!attribute.isEditable}
          />
        </Form.Item>

        {attribute.values.map((value, valIndex) => (
          <Form.Item
            key={valIndex}
            label={
              <span>
                Value {valIndex + 1}&nbsp;                       
              </span>
            }
            style={{ marginBottom: 8 }}                   
          >
            <Flex
              style={{
                display: "flex",
                paddingBottom: "20px",
                width: "100%",
              }}
              align="start"
              size={8}
            >
              <div style={{ width: "100%" }}>
                <Input.TextArea
                  value={value}
                  onChange={(e) =>
                    updateValue(index, valIndex, e.target.value)
                  }
                  maxLength={250}
                  style={{ width: "100%" }}
                  showCount={{
                    formatter: ({ count, maxLength }) =>
                      `${count}/${maxLength} characters`,
                  }}
                />
              </div>
              {attribute.values.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => removeValue(index, valIndex)}
                  style={{ width: 32, padding: 0 }}
                />
              )}
            </Flex>
          </Form.Item>
        ))}

        <div className="btnBox">
          <Button onClick={() => addValue(index)} icon={<PlusOutlined />}>
            Add a value
          </Button>
        </div>

        <div className="SwitchBox">
          <Switch defaultChecked disabled/>
          <Text className="SwitchText">Visible on PDP</Text>
        </div>

        {attribute.isEditable && (
          <div className="btnBox btnRight">
            <Button
              type="primary"
              danger
              onClick={() => removeAttribute(index)}
              style={{ marginTop: 8, marginLeft: "auto" }}
              icon={<DeleteOutlined />}
            >
              Remove Attribute
            </Button>
          </div>
        )}
      </div>
    );

    return {
      key: attribute.name,
      label,
      children,
    };
  });

  // Outer collapse items
  const outerItems = [
    {
      key: "product-page-details",
      label: "Product page details",
      children: (
        <Collapse
          defaultActiveKey={attributes.length > 0 ? [attributes[0].name] : []}
          items={attributeItems}
        />
      ),
    },
  ];

  return (
    <Collapse defaultActiveKey={["product-page-details"]} items={outerItems} />
  );
};

export default CreateKitAttributeList;